// Kerangka layar member (Figma FOLA+ MOBILE): header Beranda (logo, pemilih pohon, poin, notifikasi),
// konten responsif, dan tab bar 5 menu. Di mobile, layar turunan memakai app bar sendiri (PageTitle).
import { useQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import { Link, matchPath, NavLink, Outlet, useLocation } from 'react-router';

import { useActiveTree } from '../lib/active-tree';
import { api } from '../lib/api';
import { CLASS_LABEL } from '../lib/labels';
import { POINTS_TOTAL } from '../lib/points';
import { useSessionStatus } from '../lib/session';
import { ComplaintFooter } from './ComplaintFooter';
import { FolaLogo } from './FolaLogo';
import { BagIcon, BellIcon, HomeIcon, PlayIcon, TagIcon, UserIcon } from './icons';
import { Loading } from './ui';

const NAV = [
  { to: '/home', label: 'Beranda', Icon: HomeIcon },
  { to: '/fun', label: 'Fun', Icon: PlayIcon },
  { to: '/shop', label: 'Offer', Icon: TagIcon },
  { to: '/lifestyle', label: 'Lifestyle', Icon: BagIcon },
  { to: '/access', label: 'Access', Icon: UserIcon },
];

/** Layar di bawah menu Access (Member Area, profil, pohon, transaksi, notifikasi). */
const ACCESS_PREFIXES = [
  '/access',
  '/points',
  '/wallet',
  '/network',
  '/performance',
  '/profile',
  '/my-trees',
  '/transactions',
  '/inbox',
];
const isAccessPath = (pathname: string) =>
  ACCESS_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

/** Layar fokus (Figma tanpa tab bar): punya CTA bawah sendiri. */
const FOCUS_ROUTES = ['/wallet', '/network', '/performance', '/my-trees/:id/pay', '/my-trees/:id/upgrade'];

function TreePicker() {
  const { trees, activeTree, setActiveTreeId } = useActiveTree();
  if (!activeTree || trees.length <= 1) return null;
  return (
    <label className="min-w-0">
      <span className="sr-only">Pohon aktif</span>
      <select
        data-testid="tree-picker"
        className="min-h-9 max-w-32 truncate rounded-full border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 outline-none hover:bg-slate-50"
        value={activeTree.id}
        onChange={(event) => setActiveTreeId(event.target.value)}
      >
        {trees.map((tree) => (
          <option key={tree.id} value={tree.id}>
            {tree.name} ({CLASS_LABEL[tree.classCode] ?? tree.classCode})
          </option>
        ))}
      </select>
    </label>
  );
}

function PointsBadge() {
  return (
    <Link
      to="/points"
      className="flex items-center gap-1.5 rounded-full py-1 pr-1 pl-1 transition hover:bg-slate-50"
      title="Lihat Poin Saya"
    >
      <span className="flex size-6 items-center justify-center rounded-full bg-fola-gradient text-[11px] font-black text-white">
        P
      </span>
      <span className="flex flex-col leading-none">
        <span className="text-[13px] font-bold text-fola-ink tabular-nums">{POINTS_TOTAL}</span>
        <span className="mt-0.5 text-[9px] font-medium text-slate-400">Poin Saya</span>
      </span>
    </Link>
  );
}

function NotificationBell() {
  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api('notifications'),
    refetchInterval: 60_000,
  });
  const unread = data?.unread ?? 0;
  return (
    <Link
      to="/inbox"
      className="relative flex size-9 items-center justify-center rounded-full bg-slate-100 text-fola-ink transition hover:bg-slate-200"
      aria-label={unread > 0 ? `Notifikasi, ${unread} belum dibaca` : 'Notifikasi'}
    >
      <BellIcon className="size-[18px]" />
      {unread > 0 ? (
        <span
          aria-hidden
          className="absolute -top-0.5 -right-0.5 flex min-w-4 items-center justify-center rounded-full bg-fola-danger px-1 text-[9px] leading-4 font-bold text-white"
        >
          {unread > 99 ? '99+' : unread}
        </span>
      ) : null}
    </Link>
  );
}

function DesktopProfile() {
  const { data } = useQuery({ queryKey: ['me'], queryFn: () => api('me') });
  const first = data?.fullName.split(' ')[0] ?? '';
  return (
    <Link
      to="/profile"
      className="hidden items-center gap-2.5 rounded-full py-1 pr-2 pl-1 transition hover:bg-slate-50 md:flex"
    >
      <span className="flex size-8 items-center justify-center rounded-full bg-fola-lavender text-sm font-bold text-fola-indigo">
        {first.charAt(0) || '·'}
      </span>
      <span className="max-w-28 truncate text-sm font-medium text-fola-ink">{first}</span>
    </Link>
  );
}

/** Tamu (Beranda, pengaduan): belum ada pohon, poin, atau notifikasi; ajakan masuk di tempat yang sama. */
function GuestActions() {
  return (
    <>
      <Link
        to="/register"
        className="inline-flex min-h-9 items-center rounded-full px-3 text-xs font-bold text-fola-indigo transition hover:bg-slate-50"
      >
        Daftar
      </Link>
      <Link
        to="/login"
        className="inline-flex min-h-9 items-center rounded-full bg-fola-gradient-x px-4 text-xs font-bold text-white transition hover:opacity-95"
      >
        Masuk
      </Link>
    </>
  );
}

/** Menu "Member Area" (Figma desktop Access): sidebar di desktop, tidak tampil di mobile. */
const MEMBER_AREA = [
  { to: '/access', label: 'Ringkasan akun' },
  { to: '/points', label: 'Poin saya' },
  { to: '/wallet', label: 'Saldo & komisi' },
  { to: '/network', label: 'Jaringan saya' },
  { to: '/performance', label: 'Report performance' },
  // Layanan pengaduan konsumen (SIUPMSE); halamannya di luar Member Area agar bisa dibuka tanpa login.
  { to: '/pengaduan', label: 'Pengaduan & keluhan' },
];

export function MemberAreaLayout() {
  return (
    <div className="md:grid md:grid-cols-[13rem_1fr] md:gap-8">
      <aside className="hidden border-r border-slate-100 pr-4 md:block">
        <p className="mb-3 px-3 text-[10px] font-semibold tracking-wider text-slate-400 uppercase">
          Member area
        </p>
        <nav aria-label="Member area" className="space-y-1">
          {MEMBER_AREA.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end
              className={({ isActive }) =>
                `block rounded-lg px-3 py-2.5 text-[13px] transition ${
                  isActive
                    ? 'bg-fola-lavender font-semibold text-fola-indigo'
                    : 'text-slate-500 hover:bg-slate-50 hover:text-fola-ink'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="min-w-0">
        <Outlet />
      </div>
    </div>
  );
}

export function AppShell() {
  const { pathname } = useLocation();
  const guest = useSessionStatus() === 'anonymous';
  const isHome = pathname === '/home';
  const focus = FOCUS_ROUTES.some((pattern) => matchPath(pattern, pathname));

  return (
    <div className="flex min-h-dvh flex-col bg-white">
      {/* Header: di mobile hanya Beranda (Figma); di desktop selalu tampil dengan navigasi. */}
      <header
        className={`sticky top-0 z-30 bg-white/95 backdrop-blur-md md:border-b md:border-slate-100 ${isHome ? 'block' : 'hidden md:block'}`}
      >
        <div className="flex w-full items-center justify-between gap-2 px-4 py-3 sm:px-6 md:px-12 md:py-0">
          <Link to="/home" className="flex shrink-0 items-center md:py-3.5" aria-label="Fola+ beranda">
            <FolaLogo size="md" />
          </Link>

          {/* Navigasi desktop (Figma): teks dengan garis bawah pada menu aktif. */}
          <nav
            aria-label="Navigasi utama desktop"
            className="hidden items-stretch gap-2 self-stretch md:flex lg:gap-6"
          >
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center border-b-2 px-3 text-sm transition ${
                    isActive || (item.to === '/access' && isAccessPath(pathname))
                      ? 'border-fola-indigo font-semibold text-fola-indigo'
                      : 'border-transparent text-slate-500 hover:text-fola-ink'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex min-w-0 items-center gap-2">
            {guest ? (
              <GuestActions />
            ) : (
              <>
                <TreePicker />
                <span className="md:hidden">
                  <PointsBadge />
                </span>
                <NotificationBell />
                <DesktopProfile />
              </>
            )}
          </div>
        </div>
      </header>

      <main
        className={`w-full flex-1 px-4 pb-8 sm:px-6 md:px-12 md:pt-10 md:pb-16 ${isHome ? 'pt-1' : 'pt-0'}`}
      >
        <Suspense fallback={<Loading />}>
          <Outlet />
        </Suspense>
      </main>

      {/* Mobile: ruang bawah agar footer tidak tertutup tab bar / bar aksi layar fokus. */}
      <ComplaintFooter className="pb-28 md:pb-0" />

      {/* Tab bar mobile (Figma): ikon garis, label kapital, garis bawah pada menu aktif. */}
      {focus ? null : (
        <nav
          aria-label="Navigasi utama"
          className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-100 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md md:hidden"
        >
          <ul className="grid grid-cols-5 pt-2.5 pb-2">
            {NAV.map(({ to, label, Icon }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center gap-1 text-[9px] font-semibold tracking-wide uppercase transition ${
                      isActive || (to === '/access' && isAccessPath(pathname))
                        ? 'text-fola-indigo'
                        : 'text-slate-400 hover:text-slate-600'
                    }`
                  }
                >
                  {({ isActive: exact }) => {
                    const isActive = exact || (to === '/access' && isAccessPath(pathname));
                    return (
                      <>
                        <Icon className="size-6 transition-transform active:scale-90" strokeWidth={1.6} />
                        <span>{label}</span>
                        <span
                          aria-hidden
                          className={`h-0.5 w-8 rounded-full ${isActive ? 'bg-fola-indigo' : 'bg-transparent'}`}
                        />
                      </>
                    );
                  }}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}

/** Kerangka layar publik (masuk, daftar, lupa password). */
export function PublicShell() {
  return (
    <div className="flex min-h-dvh flex-col bg-white md:bg-slate-50/50">
      <main className="flex flex-1 flex-col md:justify-center">
        <Suspense fallback={<Loading />}>
          <Outlet />
        </Suspense>
      </main>
      <ComplaintFooter />
    </div>
  );
}

/** Splash Fola+ (Figma): latar aset resmi + logo putih, tampil saat sesi sedang disiapkan. */
export function SplashScreen() {
  return (
    <div role="status" className="flex min-h-dvh flex-col items-center justify-center bg-fola-splash">
      <FolaLogo variant="white" size="xl" />
      <span className="sr-only">Menyiapkan sesi…</span>
    </div>
  );
}
